//
// DISCLAIMER
//
// Copyright 2019-2022 ArangoDB GmbH, Cologne, Germany
//

import React, { Component } from "react";
import { Button, Table, Menu } from "semantic-ui-react";
import apiClients from "../../api/apiclients";
import {
  IDOptions as ApiIDOptions,
  ListOptions as ApiListOptions,
  TermsAndConditionsInfo as ApiTermsAndConditionsInfo,
  TermsAndConditionsInfoList as ApiTermsAndConditionsInfoList,
} from "../../api/lib";
import { RouteComponentProps } from "react-router-dom";
import { withRefresh, IWithRefreshProps } from "../../util/WithRefresh";
import { ContentSegment, ErrorMessage, ListActionEdit, ListActionSetCurrent, ListActionView, Loading, Processing, SecondaryMenu, TextLink } from "../../ui/lib";
import { hasSupportPermission, Permission } from "../../util/PermissionCache";
import { DateTimePopupWithUTCAndLocalTime } from "../../util/dateAndTimeUtils/DateTime";
import { Routes } from "../../routes";

const HeaderView = () => (
  <Table.Header>
    <Table.Row>
      <Table.HeaderCell>ID</Table.HeaderCell>
      <Table.HeaderCell>Locked</Table.HeaderCell>
      <Table.HeaderCell>Current</Table.HeaderCell>
      <Table.HeaderCell>Created at</Table.HeaderCell>
      <Table.HeaderCell>Actions</Table.HeaderCell>
    </Table.Row>
  </Table.Header>
);

interface IRowView {
  item: ApiTermsAndConditionsInfo;
  canUpdate: boolean;
  canSetCurrent: boolean;
  onView: () => void;
  onEdit: () => void;
  onSetCurrent: () => void;
}

const RowView = ({ ...args }: IRowView) => {
  const tandc = args.item.terms_and_conditions || {};
  return (
    <Table.Row>
      <Table.Cell>
        <TextLink href={Routes.dashboard_sales_terms_and_conditions_detailsWithId(tandc.id as string)}>{tandc.id}</TextLink>
      </Table.Cell>
      <Table.Cell>{args.item.locked ? "Yes" : "No"}</Table.Cell>
      <Table.Cell>{args.item.current ? "Yes" : "No"}</Table.Cell>
      <Table.Cell>{tandc.created_at ? <DateTimePopupWithUTCAndLocalTime dateTime={tandc.created_at} label="Created at" /> : "-"}</Table.Cell>
      <Table.Cell textAlign="right" collapsing>
        <div className="table-action-buttons">
          <ListActionView onClick={args.onView} />
          {args.canUpdate && !args.item.locked && <ListActionEdit onClick={args.onEdit} />}
          {args.canSetCurrent && !args.item.current && <ListActionSetCurrent onClick={args.onSetCurrent} />}
        </div>
      </Table.Cell>
    </Table.Row>
  );
};

// Interface describing the list
interface IListView {
  items: ApiTermsAndConditionsInfo[];
  canUpdate: boolean;
  canSetCurrent: boolean;
  onView: (id: string) => void;
  onEdit: (id: string) => void;
  onSetCurrent: (id: string) => void;
}

const ListView = ({ ...args }: IListView) => (
  <Table striped>
    <HeaderView />
    <Table.Body>
      {args.items.map((item) => {
        const terms_and_conditions = item.terms_and_conditions || {};
        const id = terms_and_conditions.id || "";
        return (
          <RowView {...args} key={id} item={item} onView={() => args.onView(id)} onEdit={() => args.onEdit(id)} onSetCurrent={() => args.onSetCurrent(id)} />
        );
      })}
    </Table.Body>
  </Table>
);

const EmptyView = () => <div>No terms &amp; conditions</div>;

// Interface describing the list view arguments
export interface ITermsAndConditionsListViewArgs extends RouteComponentProps {
  loading: boolean;
  list?: ApiTermsAndConditionsInfoList;
  canUpdate: boolean;
  canSetCurrent: boolean;
  onView: (id: string) => void;
  onEdit: (id: string) => void;
  onSetCurrent: (id: string) => void;
}

export const TermsAndConditionsListView = ({ ...args }: ITermsAndConditionsListViewArgs) => {
  if (!args.list) {
    return <Loading />;
  }
  const isEmpty = !args.list.items || args.list.items.length === 0;
  const items = args.list ? args.list.items : undefined;
  return (
    <div>
      <ListView {...args} items={items || []} />
      {isEmpty && <EmptyView />}
    </div>
  );
};

interface ITermsAndConditionsListProps extends IWithRefreshProps, RouteComponentProps {
  onCreate: () => void;
  onView: (id: string) => void;
  onEdit: (id: string) => void;
}

interface ITermsAndConditionsListState {
  list?: ApiTermsAndConditionsInfoList;
  settingCurrent: boolean;
  errorMessage?: string;
}

// The component to show T&Cs as a list.
class TermsAndConditionsList extends Component<ITermsAndConditionsListProps, ITermsAndConditionsListState> {
  state: ITermsAndConditionsListState = {
    list: undefined,
    settingCurrent: false,
    errorMessage: undefined,
  };

  hasPermission = (permission: Permission) => hasSupportPermission(permission, this.props.hasPermissionByUrl);

  reloadTermsAndConditions = async () => {
    var req: ApiListOptions = {};
    const list = await apiClients.idashboardClient.ListTermsAndConditions(req);
    this.setState({
      list: list,
    });
  };

  refreshTermsAndConditions = () => {
    this.props.refreshNow && this.props.refreshNow(this.reloadTermsAndConditions);
  };

  componentDidMount() {
    this.refreshTermsAndConditions();
  }

  onSetCurrent = async (id: string) => {
    try {
      this.setState({ settingCurrent: true, errorMessage: undefined });
      const req: ApiIDOptions = {
        id: id,
      };
      await apiClients.idashboardClient.SetCurrentTermsAndConditions(req);
      this.refreshTermsAndConditions();
    } catch (e) {
      this.setState({ errorMessage: e });
    } finally {
      this.setState({ settingCurrent: false });
    }
  };

  render() {
    const list = this.state.list;
    const has_create = this.hasPermission("internal-dashboard.termsandconditions.create");
    const has_set_current = this.hasPermission("internal-dashboard.termsandconditions.set-current");
    const has_update = this.hasPermission("internal-dashboard.termsandconditions.update");

    return (
      <ContentSegment>
        <Processing active={this.state.settingCurrent} message="Changing current Terms &amp; Conditions..." />
        <ErrorMessage active={!!this.state.errorMessage} message={this.state.errorMessage} />
        <ErrorMessage active={!!this.props.error} onDismiss={this.props.clearError} message={this.props.error} />
        <SecondaryMenu>
          <Menu.Item header>Terms &amp; Conditions</Menu.Item>
          <Button basic size="tiny" disabled={!has_create} onClick={this.props.onCreate} icon="plus" />
        </SecondaryMenu>
        <TermsAndConditionsListView {...this.props} list={list} canUpdate={has_update} canSetCurrent={has_set_current} onSetCurrent={this.onSetCurrent} />
      </ContentSegment>
    );
  }
}

export default withRefresh()(TermsAndConditionsList);
